import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import { type INormalizedApiError } from 'shared/api/normalizeApiError'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { AssignNumberCommonStore, PhoneApi } from 'entities/Phone'
import type { IParamsCreateBulkInboxPerNumber, IResponseNumber } from 'entities/Phone/api/types'
import { InboxesApi } from 'entities/Inbox'

import type { IEntitiesStore } from 'entities/EntitiesExport'
import { AssignInboxModal } from 'entities/Phone/ui/actions/assignInbox/ui/ui/AssignInboxModal'
import { type IAssignType } from 'entities/Phone/ui/AssignNumberCommon/store/AssignNumberCommonStore'
import { type IOnCloseAssignInboxModalModalProps } from 'pages/settings/pages/numbers/ui/UnassignedNumbers/store/UnassignedNumbersStore'

export type IAssignInboxProps = {
  numbers: IResponseNumber[]
  onCloseAssignInboxModalModal: (props: IOnCloseAssignInboxModalModalProps) => void
}

export class AssignInboxStore {
  private _modalId = nanoid()
  private readonly _assignNumberCommonStore: AssignNumberCommonStore

  constructor(
    private _numbers: IAssignInboxProps['numbers'],
    private _onCloseAssignInboxModalModal: IAssignInboxProps['onCloseAssignInboxModalModal'],
    private _entities: IEntitiesStore,
    private _withAircallBulk?: boolean
  ) {
    this._assignNumberCommonStore = new AssignNumberCommonStore(
      {
        nextClickHandler: () => {},
        allowedTabs: ['existing', 'new', 'new_per_number'],
        hideSubmit: true,
        numbers: this._numbers,
      },
      this._entities
    )
    this.openAssignInboxModalModal()

    makeAutoObservable(this)
  }

  get assignNumberCommonStore() {
    return this._assignNumberCommonStore
  }

  get assignType() {
    return this._assignNumberCommonStore.assignType
  }

  get activeInbox() {
    return this._assignNumberCommonStore.activeInbox
  }

  get disabledButton() {
    return this._assignNumberCommonStore.disabledButton
  }

  get loadingButton() {
    return this._assignNumberCommonStore.loadingButton
  }

  get numbers() {
    return this._numbers
  }

  private _resetAssignInboxState = () => {
    this._numbers = []
    this._assignNumberCommonStore.reset()
  }

  onAssignInboxModalClose = () => {
    modalStore.removeModal(this._modalId)
    this._resetAssignInboxState()
  }

  onAssignInboxModal = async () => {
    try {
      await this._entities.complianceStore.loadNumberVendors()

      const {
        processedNumbers,
        rejectedNumbers,
        rejectMessage: rejectProcessMessage,
      } = await this.processNumbers()

      if (processedNumbers.length === 0) {
        this.handleAssignNumbers({
          rejectedNumbers,
          successAssignedNumbers: [],
          rejectMessage: rejectProcessMessage,
        })
        return
      }

      const { successNumbers, additionalRejectedNumbers, rejectMessage } =
        await this.assignNumbersToInbox(processedNumbers, rejectedNumbers)

      const allRejectedNumbers = [...rejectedNumbers, ...additionalRejectedNumbers]
      const combinedRejectMessage = [...(rejectProcessMessage ?? []), ...(rejectMessage ?? [])]

      if (this.assignType !== 'new_per_number') {
        this.handleAssignNumbers({
          rejectedNumbers: allRejectedNumbers,
          successAssignedNumbers: successNumbers,
          processedAssignedNumbers: processedNumbers,
          rejectMessage: combinedRejectMessage,
        })
      }
    } catch (error) {
      logger.error(error)
    }
  }

  private async processNumbers() {
    const processedNumbers: IResponseNumber[] = []
    const rejectedNumbers: IResponseNumber[] = []
    const rejectMessage: string[] = []

    const aircallNumbers = this._numbers.filter((number) => number.is_aircall)
    const nonAircallNumbers = this._numbers.filter((number) => !number.is_aircall)

    processedNumbers.push(...nonAircallNumbers)

    if (aircallNumbers.length > 0) {
      const validationResults = await this.validateAircallNumbersBatch(aircallNumbers)

      aircallNumbers.forEach((number, index) => {
        if (validationResults[index].success) {
          processedNumbers.push(number)
        } else {
          rejectedNumbers.push(number)
          const errors = validationResults[index].errors
          if (Array.isArray(errors)) {
            rejectMessage.push(...errors)
          } else if (errors !== null && typeof errors === 'string') {
            rejectMessage.push(errors)
          }
        }
      })
    }

    return { processedNumbers, rejectedNumbers, rejectMessage }
  }

  private async validateAircallNumbersBatch(numbers: IResponseNumber[]) {
    const validationPromises = numbers.map((number) => this.validateAircallNumber(number))
    return await Promise.all(validationPromises)
  }

  private async validateAircallNumber(number: IResponseNumber) {
    try {
      await PhoneApi.checkAircallNumber(number.aircall_number_id || '')
      return { success: true }
    } catch (e) {
      return {
        success: false,
        errors: e instanceof AxiosError ? e.errors.getError('number') : null,
      }
    }
  }

  private async assignNumbersToInbox(
    processedNumbers: IResponseNumber[],
    rejectedNumbers: IResponseNumber[]
  ): Promise<{
    successNumbers: IResponseNumber[]
    additionalRejectedNumbers: IResponseNumber[]
    rejectMessage: string[] | null
  }> {
    switch (this.assignType) {
      case 'new': {
        const result = await this.assignToNewInbox(processedNumbers)
        return {
          successNumbers: result.successNumbers,
          additionalRejectedNumbers: result.rejectedNumbers,
          rejectMessage: result.rejectMessage,
        }
      }
      case 'existing': {
        const result = await this.assignToExistingInbox(processedNumbers)
        return {
          successNumbers: result.successNumbers,
          additionalRejectedNumbers: result.rejectedNumbers,
          rejectMessage: result.rejectMessage,
        }
      }
      case 'new_per_number': {
        const successNumbers = await this.assignToNewPerNumberInbox(
          processedNumbers,
          rejectedNumbers
        )
        return { successNumbers, additionalRejectedNumbers: [], rejectMessage: null }
      }
      default:
        return { successNumbers: [], additionalRejectedNumbers: [], rejectMessage: null }
    }
  }

  private async assignToNewInbox(processedNumbers: IResponseNumber[]) {
    if (this._withAircallBulk && this.assignType === 'new') {
      return await this.assignAircallBulkToNewInbox(processedNumbers)
    }

    try {
      const { data } = await InboxesApi.createInboxAndAssignNumber({
        name: this._assignNumberCommonStore.newInboxStore.newInboxName,
        owner: { id: this._assignNumberCommonStore.newInboxStore.activeInboxOwner },
        members: this._assignNumberCommonStore.newInboxStore.selectedMembersIds,
        numberIds: processedNumbers.map((number) => number.id),
        number: '',
      })

      return { successNumbers: data.numbers, rejectedNumbers: [], rejectMessage: null }
    } catch (error) {
      logger.error(error)
      return { successNumbers: [], rejectedNumbers: processedNumbers, rejectMessage: null }
    }
  }

  private parseAircallAssignmentErrors(errors: INormalizedApiError): string[] | null {
    const result = errors
      .values()
      .filter((v) => !v.value.includes('The Vendor ID'))
      .map((v) => v.value)
    return result.length > 0 ? result : null
  }

  private async assignToExistingInbox(processedNumbers: IResponseNumber[]): Promise<{
    successNumbers: IResponseNumber[]
    rejectedNumbers: IResponseNumber[]
    rejectMessage: string[] | null
  }> {
    if (!this.activeInbox) {
      return { successNumbers: [], rejectedNumbers: [], rejectMessage: null }
    }

    if (this._withAircallBulk) {
      try {
        const { data } = await InboxesApi.assignAircallNumbersToInbox(this.activeInbox.id, {
          numbers: processedNumbers.map((number) => ({
            vendor_id: number.id.toString(),
            number: number.number,
          })),
          number_vendor_id: this._entities.complianceStore.aircallNumberVendorId,
        })

        return { successNumbers: data.numbers, rejectedNumbers: [], rejectMessage: null }
      } catch (e) {
        logger.error(e)
        if (e instanceof AxiosError && e.response?.status === 422) {
          const rejectMessage = this.parseAircallAssignmentErrors(e.errors)
          return { successNumbers: [], rejectedNumbers: processedNumbers, rejectMessage }
        }
        throw e
      }
    }

    try {
      const { data } = await PhoneApi.assignInbox(this.activeInbox.id, {
        name: this.activeInbox.name,
        owner: { id: this.activeInbox.owner_id },
        members: [],
        number: '',
        numberIds: processedNumbers.map((number) => number.id),
      })

      return { successNumbers: data, rejectedNumbers: [], rejectMessage: null }
    } catch (error) {
      logger.error(error)
      return { successNumbers: [], rejectedNumbers: processedNumbers, rejectMessage: null }
    }
  }

  private async assignToNewPerNumberInbox(
    processedNumbers: IResponseNumber[],
    rejectedNumbers: IResponseNumber[]
  ) {
    if (this._withAircallBulk) {
      return await this.assignAircallPerNumber(processedNumbers, rejectedNumbers)
    }

    return await this.assignRegularPerNumber(processedNumbers, rejectedNumbers)
  }

  private async assignAircallBulkToNewInbox(processedNumbers: IResponseNumber[]) {
    try {
      const { data } = await InboxesApi.assignNumbersToInbox({
        name: this._assignNumberCommonStore.newInboxStore.newInboxName,
        number_vendor_id: this._entities.complianceStore.aircallNumberVendorId,
        owner: { id: this._assignNumberCommonStore.newInboxStore.activeInboxOwner },
        members: this._assignNumberCommonStore.newInboxStore.selectedMembersIds,
        numbers: processedNumbers.map((number) => ({
          number: number.number,
          vendor_id: number.id.toString(),
        })),
      })

      return { successNumbers: data.numbers, rejectedNumbers: [], rejectMessage: null }
    } catch (error) {
      logger.error(error)
      if (error instanceof AxiosError && error.response?.status === 422) {
        const rejectMessage = this.parseAircallAssignmentErrors(error.errors)
        return { successNumbers: [], rejectedNumbers: processedNumbers, rejectMessage }
      }
      throw error
    }
  }

  private async assignAircallPerNumber(
    processedNumbers: IResponseNumber[],
    rejectedNumbers: IResponseNumber[]
  ) {
    try {
      const rejectIds = rejectedNumbers.map((number) => number.id)
      const params =
        this._assignNumberCommonStore.newInboxPerNumberStore.getParamsAircall(rejectIds)
      const { data } = await InboxesApi.assignAircallNumbersToInboxPerNumber(params)

      this.handleCreateBulkInboxPerNumber({
        status: data.status,
        rejectAssignedNumbers: rejectedNumbers,
        processedAssignedNumbers: processedNumbers,
        successAssignedNumbers: [],
        assignType: this.assignType,
      })
    } catch (error) {
      logger.error(error)
      const rejectMessage =
        error instanceof AxiosError && error.response?.status === 422
          ? this.parseAircallAssignmentErrors(error.errors)
          : null

      this.handleCreateBulkInboxPerNumber({
        status: 'error',
        rejectAssignedNumbers: rejectedNumbers,
        processedAssignedNumbers: processedNumbers,
        successAssignedNumbers: [],
        assignType: this.assignType,
        rejectMessage,
      })
    }

    return []
  }

  private async assignRegularPerNumber(
    processedNumbers: IResponseNumber[],
    rejectedNumbers: IResponseNumber[]
  ) {
    const bulk_id = nanoid()
    this._entities.numbersStore.addBulkInboxPerNumberIds(bulk_id)

    const params: IParamsCreateBulkInboxPerNumber = {
      bulk_id,
      teams: this._assignNumberCommonStore.newInboxPerNumberStore.params.teams.filter(
        (item) => !rejectedNumbers.find((rejected) => rejected.id === item.numberIds[0])
      ),
    }

    try {
      const { data } = await PhoneApi.createBulkInboxPerNumber(params)

      this.handleCreateBulkInboxPerNumber({
        status: data.status,
        rejectAssignedNumbers: rejectedNumbers,
        processedAssignedNumbers: processedNumbers,
        successAssignedNumbers: [],
        assignType: this.assignType,
      })

      return []
    } catch (error) {
      logger.error(error)
      const rejectMessage =
        error instanceof AxiosError && error.response?.status === 422
          ? this.parseAircallAssignmentErrors(error.errors)
          : null

      this.handleCreateBulkInboxPerNumber({
        status: 'error',
        rejectAssignedNumbers: rejectedNumbers,
        processedAssignedNumbers: processedNumbers,
        successAssignedNumbers: [],
        assignType: this.assignType,
        rejectMessage,
      })
      return []
    }
  }

  private openAssignInboxModalModal = () => {
    if (!this._entities.usersStore.users.length) {
      this._entities.usersStore.initUsersOrganization()
    }

    modalStore.addModal({
      id: this._modalId,
      width: 580,
      title: 'Assign number',
      type: ModalTypeList.DEFAULT,
      ModalContent: AssignInboxModal,
      ModalContentProps: {
        assignInboxStore: this,
      },
      paddingContent: '0 24px 16px 24px',
      onClose: this.onAssignInboxModalClose,
      showCloseButton: false,
    })
  }

  handleAssignNumbers = ({
    rejectedNumbers,
    successAssignedNumbers,
    processedAssignedNumbers,
    rejectMessage,
  }: {
    rejectedNumbers: IResponseNumber[]
    successAssignedNumbers: IResponseNumber[]
    processedAssignedNumbers?: IResponseNumber[]
    rejectMessage: string[] | null
  }) => {
    this._onCloseAssignInboxModalModal?.({
      rejectAssignedNumbers: rejectedNumbers,
      successAssignedNumbers,
      processedAssignedNumbers,
      rejectMessage,
    })

    if (successAssignedNumbers.length) {
      showToast({
        type: 'success',
        title: `Number${successAssignedNumbers.length ? 's' : ''} assigned to inbox`,
      })
    }

    this.onAssignInboxModalClose()
  }

  handleCreateBulkInboxPerNumber = ({
    status,
    processedAssignedNumbers,
    rejectAssignedNumbers,
    successAssignedNumbers,
    assignType,
    rejectMessage,
  }: {
    status: 'success' | 'error'
    processedAssignedNumbers: IResponseNumber[]
    rejectAssignedNumbers: IResponseNumber[]
    successAssignedNumbers: IResponseNumber[]
    assignType?: IAssignType
    rejectMessage?: string[] | null
  }) => {
    this.onAssignInboxModalClose()

    this._onCloseAssignInboxModalModal?.({
      processedAssignedNumbers,
      rejectAssignedNumbers,
      successAssignedNumbers,
      assignType,
      rejectMessage,
    })

    if (status === 'success') {
      showToast({
        type: 'info',
        title: 'Setting up your inbox',
        desc: 'We’re creating your inboxes and assigning numbers. You’ll be notified when it’s ready.',
      })
    }
  }

  get idsLength() {
    return this._numbers.length
  }
}
